import lodash from 'lodash'
import * as Selectors from '../../src/redux/reducers'
import type { Procedure } from '../../../app-core/lib/procedure/Procedure'
import { globalHive } from '../../src/modules/hive'
import { setReportEvent } from '../../src/redux/actions/activity'
import { syncCalendarEvents } from '../../src/redux/actions/entities'
import { Acti_SET_ACTION_TYPES, Acti_SET_ACTIVITY, Acti_SET_STORE } from './Acti.constants'
import { Acti_dispatch } from './Acti.dispatch'
import { Base_Dispatcher } from '../Base/Base.Dispatcher'
import { Flow_writeFlowActivityDefaultAnswers } from '../Flow/Flow.answer'
import { I18N_localize } from '../I18N/I18N.localize'
import { IDB_actionTypesReadAll } from '../IDB/IDB.table.actionType'
import { IDB_calendarEventRead, IDB_calendarEventsWrite } from '../IDB/IDB.table.calendarEvent'
import { IDB_procedureRead } from '../IDB/IDB.table.procedure'
import { Loca_SET_DIALOG_CAMPAIGNS, Loca_SET_DIALOG_OPEN, Loca_SET_DIALOG_TYPE } from '../Loca/Loca.constants'
import { Logg_event } from '../Logg/Logg.event'
import { Proc_getValidForLocation } from '../Proc/Proc.getValid'
import { Rout_replaceWithActivityBase } from '../Rout/Rout.activity'
import { Rout_toLoading } from '../Rout/Rout.router'
import { Rout_back } from '../Rout/Rout'
import { Rpts_openReportWrite } from '../Rpts/Rpts.OpenReport'
import { Rpts_createClientId } from '../Rpts/Rpts.types'
import type { Todo_ScheduledInstance } from '../Todo/Todo.ScheduledInstance'
import { UI_dialogOk, UI_dialogOpen } from '../UI/UI.Dialog'
import { Util_isoDateNow, Util_isoDateString } from '../Util/Util.date'
import type { Type_ICalendarEvent, Type_ILocation, Type_ILocationFocusList } from '../Type/Type.hive'
import { isFlowWorkflow } from '../../../app-core/lib/workflow/Workflow'
import { WF_fetch } from '../WF/WF.fetch'
import { Acti_getFlowsInActivities } from './Acti.flow'
import { Flow_fetchUnitsInFlows } from '../Flow/Flow.units'
import { getFlowUnitIds } from '../../../shared/domain/flows/FlowUnitsIds.no-deps'
import { dictMerge, emptyDict } from '../../../shared/lib/functional/dictionary.no-deps'
import type { ClientLogger } from '../../../shared/lib/ClientLogger.no-deps'
import { globalReduxStore } from '../../src/modules/initStore'
import { Translations } from '../../../shared/i18n/Translations'
import type { LoopPlan } from '../../../shared/domain/flows/LoopPlan.no-deps'
import { isNotNil } from '../../../shared/lib/functional/nil.no-deps'

export async function Acti_startActivity(
    logger: ClientLogger,
    location: Type_ILocation,
    eventId: number | string,
    scheduledInstance?: Todo_ScheduledInstance,
    teamDoneFor?: number,
    origin?: number
) {
    Logg_event('Start', { label: 'activity.startActivity' })

    Rout_toLoading()

    const state = globalHive.getState()

    const event = await IDB_calendarEventRead(eventId).catch<Type_ICalendarEvent>(() => null)
    if (!event) return Rout_back()

    const campaignsList = Selectors.getEntityArray(state, 'campaigns')
    const focusList = Selectors.getEntityArray(state, 'focusLists')

    // we ideally want always create events with campaigns so that we dont have to do this check twice
    const isEventValid = await validateEvent(event, campaignsList, focusList, location.id)
    if (!isEventValid) return Rout_back()

    // Set the workflow
    const workflow = await WF_fetch(logger, event.template, { includeDeleted: false })
    if (workflow == null) {
        UI_dialogOpen(
            {
                title: Translations.t('app:common.somethingWentWrong'),
            },
            I18N_localize('activity.invalidEventNoWorkflow', [event.id])
        )
        return Rout_back()
    }

    // Ensure it's an activity workflow
    if (isFlowWorkflow(workflow)) {
        logger.error('Cannot start flow workflow', { workflow })
        return Rout_back()
    }

    // Sort activities by position
    workflow.activities = lodash.sortBy(workflow.activities, (item) => {
        return item.position
    })

    // Unlock the first item and lock the rest
    workflow.activities = workflow.activities.map((activity, index) => {
        if (index === 0) {
            return {
                ...activity,
                checked: false,
                completed: false,
                blocked: false,
                selected: true,
            }
        }
        const allDependenciesCompleted = activity.dependencies.every((dependency) => {
            const requiredAction = workflow.activities.find((a) => a.id === dependency)
            return requiredAction && requiredAction.completed
        })
        return {
            ...activity,
            checked: false,
            completed: false,
            blocked: !allDependenciesCompleted,
            selected: false,
        }
    })

    const activityIds = workflow.activities.map((a) => a.id)
    const actionTypes = await IDB_actionTypesReadAll().then((ats) => ats.filter((at) => activityIds.includes(at.id)))

    const procedure = await IDB_procedureRead(event.campaign)
    const startDate = Util_isoDateNow()
    const clientId = Rpts_createClientId()

    const flows = Acti_getFlowsInActivities(actionTypes)
    const allUnits = await Flow_fetchUnitsInFlows(flows)
    const flowUnitIds = flows.map((flow) => getFlowUnitIds(allUnits, flow)).reduce(dictMerge, emptyDict())

    const { lat, lon } = Selectors.getCoordinates(globalReduxStore.getState())

    const loopPlan: LoopPlan = []

    await Rpts_openReportWrite({
        clientId,
        startDate,
        actionTypes,
        calendarEvent: event,
        location,
        procedure,
        workflow,
        scheduledInstance: scheduledInstance && scheduledInstance.id,
        dueDate: scheduledInstance && Util_isoDateString(scheduledInstance.dueDate.toISOString()),
        teams: scheduledInstance ? scheduledInstance.teams : null,
        showOverdue: scheduledInstance ? scheduledInstance.showOverdue : false,
        exhaustiveAnswers: {},
        description: scheduledInstance && scheduledInstance.description,
        teamDoneFor,
        origin,
        isAction: isNotNil(origin),
        flowUnitIds,
        lat,
        lon,
    })

    await Flow_writeFlowActivityDefaultAnswers(clientId, new Date(startDate.value), flows, allUnits, loopPlan)

    globalHive.dispatch(setReportEvent(event))
    Acti_dispatch({ actionType: Acti_SET_STORE, data: location })
    Acti_dispatch({ actionType: Acti_SET_ACTIVITY, data: workflow })
    Acti_dispatch({ actionType: Acti_SET_ACTION_TYPES, data: actionTypes })
    Rout_replaceWithActivityBase(location.id)
}

async function validateEvent(
    event: Type_ICalendarEvent,
    campaignsList: Procedure[],
    focusListLookup: Type_ILocationFocusList[],
    locationId: number
) {
    if (event.campaign) {
        return true
    }

    const validCampaigns = Proc_getValidForLocation(campaignsList, focusListLookup, locationId, new Date())

    if (validCampaigns.length === 1) {
        event.campaign = validCampaigns[0].id
        await IDB_calendarEventsWrite([{ ...event, meta: { updated: true } }])
        globalHive.dispatch(syncCalendarEvents())
        return true
    }

    if (validCampaigns.length > 1) {
        Base_Dispatcher.dispatch({ actionType: Loca_SET_DIALOG_CAMPAIGNS, data: validCampaigns })
        Base_Dispatcher.dispatch({ actionType: Loca_SET_DIALOG_TYPE, data: 'campaign' })
        Base_Dispatcher.dispatch({ actionType: Loca_SET_DIALOG_OPEN, data: true })
        return false
    } else {
        UI_dialogOk(Translations.t('app:common.warning'), Translations.t('app:report.noAvailableCampaign'))
        return false
    }
}
